import { type TextStyles } from '#app/theme/textStyles.js';
import { StyledButton } from './Button.styles.js';
import { StyledButtonContainer } from './ButtonContainer.styles.js';
import {
  StyledButtonGradientBackground,
  StyledButtonGradientBackgroundContraPro,
} from './ButtonGradientBackground.styles.js';
import { ButtonIcon } from './ButtonIcon.js';
import { ButtonLoadingSpinner } from './ButtonLoadingSpinner.styles.js';
import { StyledButtonText } from './ButtonText.styles.js';
import {
  type ButtonIconPlacement,
  type ButtonSize,
  type ButtonVariant,
  type StyledSystemButtonProps,
} from './types.js';
import { type EmojiSvgProps } from '@contra/icons/emojis';
import { type IconSvgProps } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import {
  Children,
  type ComponentType,
  type ElementType,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import {
  type PolymorphicForwardRefExoticComponent,
  type PolymorphicPropsWithoutRef,
} from 'react-polymorphic-types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ButtonDefaultElement = 'button';

export type ButtonOwnProps = Omit<StyledSystemButtonProps, 'size'> & {
  block?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  icon?: ComponentType<EmojiSvgProps> | ComponentType<IconSvgProps>;
  iconPlacement?: ButtonIconPlacement;
  isLoading?: boolean;
  /**
   * @deprecated use `children` instead.
   */
  label?: ReactNode;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onMouseOut?: (event: MouseEvent<HTMLElement>) => void;
  onMouseOver?: (event: MouseEvent<HTMLElement>) => void;
  removeMinWidth?: boolean;
  size?: ButtonSize;
  textStyle?: TextStyles;
  type?: 'button' | 'submit';
  variant?: ButtonVariant;
  zIndex?: number;
};

type ButtonProps<C extends ElementType = typeof ButtonDefaultElement> =
  PolymorphicPropsWithoutRef<ButtonOwnProps, C>;

const getButtonTextStyle = ({ size }: { size: ButtonSize }): TextStyles => {
  switch (size) {
    case 'small':
      return 'text-sm-semi-bold';

    case 'xsmall':
      return 'text-sm-semi-bold';

    default:
      return 'subtitleRegular';
  }
};

// @ts-expect-error - PolymorphicForwardRefExoticComponent expects a ReactElement but forwardRef returns a ReactNode.
// This component had been typed as Any for a while due to the type exports being broken, so at least it has some types now.
export const Button: PolymorphicForwardRefExoticComponent<
  ButtonOwnProps,
  typeof ButtonDefaultElement
> = forwardRef(
  <C extends ElementType = typeof ButtonDefaultElement>(
    {
      as,
      block = false,
      children,
      disabled = false,
      icon,
      iconPlacement = 'left',
      isLoading = false,
      label,
      onBlur,
      onFocus,
      onMouseOut,
      onMouseOver,
      removeMinWidth = false,
      size = 'medium',
      textStyle,
      type = 'button',
      variant = 'V2Primary',
      ...props
    }: ButtonProps<C>,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const [isFocused, setFocused] = useState(false);
    const [isHovered, setHovered] = useState(false);
    const isDisabled = disabled;

    const handleOnBlur = useCallback(
      (event: FocusEvent<HTMLElement>) => {
        setFocused(false);
        if (onBlur) onBlur(event);
      },
      [onBlur, setFocused],
    );

    const handleOnFocus = useCallback(
      (event: FocusEvent<HTMLElement>) => {
        setFocused(true);
        if (onFocus) onFocus(event);
      },
      [onFocus, setFocused],
    );

    const handleOnMouseOut = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        setHovered(false);
        if (onMouseOut) onMouseOut(event);
      },
      [onMouseOut, setHovered],
    );

    const handleOnMouseOver = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        setHovered(true);
        if (onMouseOver) onMouseOver(event);
      },
      [onMouseOver, setHovered],
    );

    const [mounted, setMounted] = useState(false);

    useMount(() => {
      setMounted(true);
    });

    return (
      <StyledButton
        $block={block}
        $isDisabled={isDisabled}
        $isFocused={isFocused}
        $isHovered={isHovered}
        $isLoading={isLoading}
        $removeMinWidth={removeMinWidth}
        as={as as undefined}
        disabled={isDisabled || isLoading || !mounted}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onMouseOut={handleOnMouseOut}
        onMouseOver={handleOnMouseOver}
        ref={ref}
        size={size}
        type={as === 'button' || as === undefined ? type : undefined}
        variant={variant}
        {...props}
        // This is ugly fix to not pass these default html props to the styled component.
        height={undefined}
        width={undefined}
      >
        {isLoading && (
          <ButtonLoadingSpinner
            size={size}
            variant={variant}
          />
        )}
        <StyledButtonContainer
          $isLoading={isLoading && variant !== 'V2Download'}
          as="span"
          size={size}
        >
          {iconPlacement === 'left' && icon && (
            <ButtonIcon size={size}>{icon}</ButtonIcon>
          )}
          {label || Children.count(children) ? (
            <StyledButtonText
              $isDisabled={isDisabled}
              $isFocused={isFocused}
              $isHovered={isHovered}
              $isLoading={isLoading}
              textStyle={textStyle ? textStyle : getButtonTextStyle({ size })}
              variant={variant}
            >
              {label ?? children}
            </StyledButtonText>
          ) : null}
          {iconPlacement === 'right' && icon && (
            <ButtonIcon size={size}>{icon}</ButtonIcon>
          )}
          {variant === 'V2Gradient' && (
            <StyledButtonGradientBackground
              $isDisabled={isDisabled}
              $isHovered={isHovered}
            />
          )}

          {variant === 'V2GradientContraPro' && (
            <StyledButtonGradientBackgroundContraPro
              $isDisabled={isDisabled}
              $isHovered={isHovered}
            />
          )}
        </StyledButtonContainer>
      </StyledButton>
    );
  },
);
